import { useEffect, useRef, useState } from 'react';
import './flip-clock.css';

const FLIP_MS = 440; // fold 0.18s + (delay 0.18s + unfold 0.26s) — matches flip-clock.css

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * FlipDigit — a single split-flap digit. Tracks the previous glyph and runs one
 * fold/unfold cycle whenever `value` changes: the static top always shows the NEW
 * glyph (revealed as the old top folds away); the static bottom shows the OLD glyph
 * until the new bottom flap unfolds over it. Reduced-motion swaps instantly.
 *
 * The change is detected during render (React's "store info from previous renders"
 * pattern) rather than in an effect, to avoid cascading renders.
 *
 * @param {{ value: string }} props single character to display
 */
function FlipDigit({ value }) {
  const [prevValue, setPrevValue] = useState(value);
  const [previous, setPrevious] = useState(value);
  const [flipping, setFlipping] = useState(false);
  const timerRef = useRef(null);

  if (value !== prevValue) {
    const animate = !prefersReducedMotion();
    setPrevious(animate ? prevValue : value);
    setPrevValue(value);
    setFlipping(animate);
  }

  useEffect(() => {
    if (!flipping) return undefined;
    timerRef.current = setTimeout(() => setFlipping(false), FLIP_MS);
    return () => clearTimeout(timerRef.current);
  }, [flipping]);

  const current = value;

  return (
    <span className={`clock-cell${flipping ? ' is-flipping' : ''}`}>
      <span className="clock-half top">
        <span className="clock-glyph">{current}</span>
      </span>
      <span className="clock-half bottom">
        <span className="clock-glyph">{flipping ? previous : current}</span>
      </span>
      {flipping && (
        <>
          <span className="clock-half top flap" key={`t-${previous}`}>
            <span className="clock-glyph">{previous}</span>
          </span>
          <span className="clock-half bottom flap" key={`b-${current}`}>
            <span className="clock-glyph">{current}</span>
          </span>
        </>
      )}
    </span>
  );
}

/** A two-digit group (e.g. hours), each digit flipping independently. */
function FlipGroup({ value, small = false }) {
  const [tens, ones] = value;
  return (
    <span className={`clock-group${small ? ' clock-group--sm' : ''}`}>
      <FlipDigit value={tens} />
      <FlipDigit value={ones} />
    </span>
  );
}

/**
 * FlipClock — a minimal architect's split-flap clock (12-hour with AM/PM). Thin
 * monospace numerals in hairline cells over a drafting dimension line; the cards
 * fold on each change. Purely a display of `date`; ticking lives in useClock.
 * aria-hidden — the readable time is the hero's sr-only label.
 *
 * @param {{ date: Date }} props the time to render
 */
export default function FlipClock({ date, compact = false }) {
  const hours = String(date.getHours() % 12 || 12).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const meridiem = date.getHours() < 12 ? 'AM' : 'PM';

  // Compact (header) variant drops the seconds group and the dimension rule so
  // it sits quietly in the toolbar and only flips once a minute.
  return (
    <div className={`clock${compact ? ' clock--compact' : ''}`} role="timer" aria-hidden="true">
      <div className="clock-row">
        <FlipGroup value={hours} />
        <span className="clock-colon">:</span>
        <FlipGroup value={minutes} />
        {!compact && (
          <>
            <span className="clock-colon">:</span>
            <FlipGroup value={seconds} small />
          </>
        )}
        <span className="clock-mer">{meridiem}</span>
      </div>
      {!compact && <span className="clock-rule" aria-hidden="true" />}
    </div>
  );
}
